/**
 * rowSet — THE derived row set for both template lists (F-31).
 *
 * Five consumers were reading four different arrays. The tree and its counts were built from
 * `queried` (search only), the table rendered `visible` (search + the retired status chip), Force
 * sync swept `visible` but reported `templates.length` in its summary, the bulk bar selected out
 * of the raw `templates` array — so it acted on rows the filter had hidden — and the
 * breadcrumb said "Select a model" over whichever of them happened to be on screen. Every one
 * of those is the same bug, and `visibleRows` is the single answer: the table, the cards, the
 * tree, the tree's counts, Force sync, the bulk bar, the breadcrumb and the "N selected"
 * counter all read THAT array and only that array.
 *
 * Pure and DOM-free, so the rules are unit-tested without mounting anything.
 */
import type { ProductTemplate } from "@genz/platform";
import {
  brandOf,
  categoryLabel,
  facetMatches,
  headStatusOf,
  modelLabel,
  styleLabel,
} from "./templateLabels.js";

export interface RowSetInput {
  templates: ProductTemplate[];
  /** The page's single search box, already lower-cased and trimmed. */
  query: string;
  /** Per-device "removed from this device" ids. Per D-28 these are NEVER dropped — they are
   *  carried through and rendered with a state, on BOTH lists. The set is passed so the
   *  caller can style them; `visibleRows` does not filter on it. */
  hiddenIds: ReadonlySet<string>;
  /** D-4 (iii): an admin sees every status; a customer sees Active only. */
  canSeeAllStatuses: boolean;
}

/**
 * The ONE search field set: name, code, brand, model, style, product — the fields the row
 * actually SHOWS, built from the FORMATTED strings.
 *
 * Two consequences, both deliberate. A customer who types "Phone case (TPU)" finds what the
 * row displays, rather than having to know the `tpu_phone_case` key. And no id can ever
 * match: `modelLabel` / `styleLabel` / `brandOf` all refuse to render a UUID, and the ids
 * are not concatenated in. Before this, the customer page searched
 * name/code/model/style/product and the editor page searched
 * name/code/display.model/display.style, so the same query returned different rows on two
 * views of one table (F-15).
 */
export function rowSearchText(t: ProductTemplate): string {
  const parts = [
    t.name,
    t.code,
    brandOf(t),
    modelLabel({ model: t.display?.model }),
    styleLabel(t.display?.style),
    categoryLabel(t.display?.category),
  ];
  return parts
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/**
 * The one derived set: `templates` → status gate → query, in that order.
 *
 * The status GATE is a PERMISSION decision (D-4 iii: a customer sees Active only) and is the
 * only status rule left here. WS-K6 removed the second one — the chip row's `facet` — because
 * the page carried TWO independent status states ANDed together: the chip narrowed here and
 * the Status dropdown narrowed in `applyFacets`, so dropdown `Active` + chip `Draft` showed
 * zero rows while both controls still displayed their own answer, and `Clear all filters`
 * could not reach the chip. `facetSelection.status` is the single status control now, the
 * dropdown's own group reads `headStatusOf` through `browseFilters.facetsOf`, and F-33 (a
 * Draft head above an Active version belongs under Draft) is pinned there.
 *
 * `hiddenIds` is an input that changes NOTHING about membership. D-28 says never hide a row
 * that was removed from this device; show it with a state, on both lists. The admin table
 * used to filter them out while the customer table showed them, so the two lists reported
 * different totals for the same account on the same machine (F-13).
 */
export function visibleRows(input: RowSetInput): ProductTemplate[] {
  const { templates, query, canSeeAllStatuses } = input;

  const gated = canSeeAllStatuses
    ? templates
    : templates.filter((t) => facetMatches("Active", headStatusOf(t)));

  const q = query.trim().toLowerCase();
  return q ? gated.filter((t) => rowSearchText(t).includes(q)) : gated;
}

/**
 * How many rows each stage removed — so the page can LOG a hidden row's reason instead of
 * letting it disappear silently. A customer whose synced Draft is gated out of the cloud
 * browse list still sees it in the Mockup Generator, whose source is the LOCAL store
 * (`mockups/BatchContents.tsx`, `listSyncedTemplates(io)`): the template is filtered, not
 * lost, and the count says so.
 */
export function rowSetCounts(input: RowSetInput): {
  total: number; gatedByStatus: number; hiddenByQuery: number; visible: number;
} {
  const { templates, canSeeAllStatuses } = input;
  const gated = canSeeAllStatuses
    ? templates
    : templates.filter((t) => facetMatches("Active", headStatusOf(t)));
  // The query stage is whatever `visibleRows` removed beyond the gate — derived rather than
  // recomputed, so the two can never disagree about what is on screen.
  const visible = visibleRows(input);
  return {
    total: templates.length,
    gatedByStatus: templates.length - gated.length,
    hiddenByQuery: gated.length - visible.length,
    visible: visible.length,
  };
}
